// src/components/Step1ProjectInfo.jsx
import React, { useEffect, useState } from "react";
import { api as axios } from "../config/apiConfig";
import { API_ORIGIN } from "../config/apiConfig";
import { useNavigate } from "react-router-dom";
import useAutoResizeTextareas from "../utils/useAutoResizeTextareas";
import { ROUTES } from "../config/routes";
import RichTextEditor from "./RichTextEditor";

// The theme's cost-estimation.html mockup shows these seven sections as
// tabs within one page. This app instead gives each one its own route
// (Step1..Step5, BasisOfCost, ElementScopePage1) with real Next/Back
// buttons that save to the backend as you go -- Step2+ expect
// localStorage("project_db_id") to already exist from Step1's save, for
// instance. So unlike the theme's tabs, only "Project" (this page) is a
// real, clickable step here; the rest are shown for visual parity with
// the mockup's step indicator but are inert -- wiring them up to jump
// straight into the middle of the wizard would let someone skip the
// save that step depends on.
const WIZARD_STEPS = [
  { key: "project", icon: "ti-file-description", label: "Project", sub: "Info & Overview" },
  { key: "budget", icon: "ti-currency-riyal", label: "Budget", sub: "Amount & Summary" },
  { key: "asset", icon: "ti-building", label: "Asset", sub: "Selection & Element" },
  { key: "element", icon: "ti-list-check", label: "Element", sub: "Main & Sub Element" },
  { key: "estimation", icon: "ti-report-money", label: "Estimation", sub: "Amount & Summary" },
  { key: "basis", icon: "ti-clipboard-text", label: "Basis of Cost", sub: "Assumption & Exclusion" },
  { key: "construction", icon: "ti-hammer", label: "Construction", sub: "Scope" },
];

// Which of the 7 wizard steps this PAGE is. Steps before this one get
// the theme's own light-green "done" tint (.wizard-item-done -- built
// into app.min.css, so this part always rendered correctly); THIS step
// gets the solid brand-green "current" highlight (.wizard-item-current,
// defined in src/styles/themeOverrides.css -- see Layout.jsx for why
// that file, not appLayout.css, is what actually loads it); steps after
// this one stay inert (.disabled).
const CURRENT_STEP_KEY = "project";
const CURRENT_STEP_INDEX = WIZARD_STEPS.findIndex((s) => s.key === CURRENT_STEP_KEY);

// FIX: was hardcoded to "/api" -- every other page
// derives this from API_ORIGIN (src/config/apiConfig.js) so switching
// hosts/HTTPS is a one-file change; this page had been missed.
const API_BASE = API_ORIGIN;

// Local YYYY-MM-DD for "today", built from date parts rather than
// `new Date().toISOString()` (which is UTC and can read as yesterday
// or tomorrow depending on the browser's timezone). The Date field
// below is disabled/read-only and always shows this -- per request,
// the estimation date isn't user-editable, same as "Estimated by" is
// always the logged-in user rather than a free-text field.
function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function Step1ProjectInfo() {
  useAutoResizeTextareas();
  const navigate = useNavigate();

  // The logged-in user, cached in localStorage("currentUser") right
  // after /verify-otp (see OtpVerify.jsx) as
  // {id, email, UserName, RoleID, RoleName}. "Estimated by" is locked
  // to this person -- it's no longer a dropdown listing every user in
  // the system, it just shows whoever is actually logged in right now.
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
  const estimatedByName = currentUser?.UserName || currentUser?.email || "Not logged in";
  const estimatedByEmail = currentUser?.email || localStorage.getItem("user_email") || "";

  const [opcos, setOpcos] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  // Styled in-page error banner (see the render below, right under the
  // wizard tabs) -- replaces the native browser alert() this page used
  // to call for every validation/server failure, same "alert alert-danger"
  // pattern Step5Final.jsx already uses for its own save errors.
  const [serverError, setServerError] = useState(null);
  // Success banner for the standalone "Save" button (see submitStep1
  // below) -- Next never needed one of its own since a successful Next
  // just navigates away, but Save stays on the page, so it needs its own
  // feedback that the click actually did something.
  const [saveMessage, setSaveMessage] = useState(null);
  // "Last saved <time>" next to the Save button -- same convention as
  // Step5Final.jsx's own lastSavedInfo, now applied to every wizard page.
  const [lastSavedTime, setLastSavedTime] = useState(null);
  // The project ID this project was actually saved under, captured only
  // when this page loaded an existing project (via ProjectList.jsx's Edit
  // action, which sets localStorage "project_db_id"). Sent back to the
  // backend as original_projectid so it can tell "re-saving project X
  // under its own ID" (fine) apart from "a new/renamed project wants an
  // ID that's already taken by someone else" (blocked) -- see goNext().
  const [originalProjectID, setOriginalProjectID] = useState("");

  const [form, setForm] = useState({
    projectName: "",
    projectID: "",
    date: todayISO(),
    opco: "",
    class: "",
    C_ID: "",
    intro: "",
    inflation: "",
    financial: "",
    contingency: "",
    risks: "",
    opportunities: "",
  });

  // "Proposed Doc" -- replaces the old "Estimated by" field in this same
  // grid slot. A single PDF (<=10MB) attached to the project, saved
  // server-side under DOCUMENT_ROOT/<CET_ID>/ as "<CET_ID>-Proposed-<n>
  // .pdf" (POST /projects/proposed_doc) -- n increments on every
  // re-upload during edit rather than overwriting, so past versions stay
  // on disk. Upload is only possible once the project has actually been
  // saved (CET_ID doesn't exist before that -- see originalProjectID
  // below), same constraint Basis of Cost's own file upload has.
  const [proposedDoc, setProposedDoc] = useState({ filename: "", version: 0 });
  // Shows the upload control instead of the VIEW/Replace row -- true
  // before any doc has ever been uploaded, or after clicking "Replace".
  const [showProposedUpload, setShowProposedUpload] = useState(true);
  const [proposedDocUploading, setProposedDocUploading] = useState(false);
  const [proposedDocError, setProposedDocError] = useState(null);
  // FIX, per explicit request: attaching a file has zero dependency on
  // Project Name / ID / Class / OpCo, or on the project having been
  // saved at all -- picking a file before the project exists no longer
  // triggers any save attempt. It's just held here until a real project
  // row (and therefore a server-side folder to put it in) exists;
  // submitStep1 uploads it automatically the moment that save succeeds,
  // by any route (Save, Next, or the auto-save this page used to do on
  // its own). See handleProposedDocChange/uploadProposedDoc below.
  const [pendingProposedDocFile, setPendingProposedDocFile] = useState(null);
  const PROPOSED_DOC_MAX_BYTES = 10 * 1024 * 1024;

  useEffect(() => {
    axios.get(`${API_BASE}/opco`).then((res) => setOpcos(res.data.data || []));
    axios.get(`${API_BASE}/class`).then((res) => setClasses(res.data.data || []));

    const saved = JSON.parse(localStorage.getItem("step1"));
    if (saved) {
      setForm({
        ...saved,
        // Resuming a draft or editing an existing project: keep
        // whatever date was saved -- past or future -- instead of
        // overwriting it with today. Only fall back to today if that
        // draft somehow has no date at all.
        date: saved.date || todayISO(),
      });

      // "project_db_id" is only ever set for a project that's actually
      // been saved (ProjectList.jsx's Edit action, or this same page's
      // own goNext() after a successful save) -- a brand-new, not-yet-
      // saved draft never has it. Only in that case is saved.projectID a
      // real, already-taken ID this save is allowed to reuse.
      if (localStorage.getItem("project_db_id")) {
        setOriginalProjectID(saved.projectID || "");
      }
    }
  }, []);

  // Load any already-uploaded Proposed Doc once this page knows it's
  // showing an actually-saved project (editing an existing one, or right
  // after this page's own first save below) -- shows the VIEW/Replace
  // row immediately instead of an upload control the user would just
  // have to skip past.
  useEffect(() => {
    if (!originalProjectID) return;
    axios
      .get(`${API_BASE}/projects/proposed_doc`, { params: { Projectid: originalProjectID } })
      .then((res) => {
        if (res.data?.success && res.data?.found) {
          setProposedDoc({ filename: res.data.filename, version: res.data.version });
          setShowProposedUpload(false);
        }
      })
      .catch((err) => console.error("Failed to load proposed document", err));
  }, [originalProjectID]);

  // File-format/size checks only -- these are inherent to the file
  // itself, not a "the project must be filled in" dependency, so they
  // still apply no matter when the upload actually happens.
  const validateProposedDocFile = (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (ext !== "pdf") return "Only PDF files are allowed.";
    if (file.size > PROPOSED_DOC_MAX_BYTES) return "File exceeds the 10 MB limit.";
    return null;
  };

  // The actual POST /projects/proposed_doc call, factored out so both
  // the "project already exists" path below and submitStep1's "a file
  // was waiting for a project to exist" path can share it. Returns
  // true/false instead of throwing so callers can decide what to do
  // next (submitStep1 keeps the file pending on failure so the next
  // successful save retries it, rather than silently losing it).
  const uploadProposedDoc = async (file, projectId) => {
    setProposedDocUploading(true);
    try {
      const formData = new FormData();
      formData.append("Projectid", projectId);
      formData.append("file", file);
      const res = await axios.post(`${API_BASE}/projects/proposed_doc`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.success) {
        setProposedDoc({ filename: res.data.filename, version: res.data.version });
        setShowProposedUpload(false);
        return true;
      }
      setProposedDocError(res.data?.error || "Failed to upload the file.");
      return false;
    } catch (err) {
      console.error(err);
      setProposedDocError(err.response?.data?.error || "Server error while uploading the file.");
      return false;
    } finally {
      setProposedDocUploading(false);
    }
  };

  // FIX, per explicit request: attaching a file now has NO dependency
  // on Project Name / ID / Class / OpCo, or on the project having ever
  // been saved -- selecting a file never triggers a save attempt and
  // never checks any other field. If the project already exists
  // (originalProjectID set), it uploads immediately, same as always. If
  // it doesn't exist yet, there is genuinely no project row/folder on
  // the server to attach it to, so instead of saving anything on the
  // user's behalf (what an earlier version of this fix did) or blocking
  // the picker (what the original code did), the file is simply held
  // locally in pendingProposedDocFile and shown as "selected, will
  // attach on save" (see the render code below). submitStep1 uploads it
  // automatically the moment a save actually succeeds, whenever and
  // however that happens.
  const handleProposedDocChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // lets the same file be re-picked later if needed
    if (!file) return;

    setProposedDocError(null);

    const validationError = validateProposedDocFile(file);
    if (validationError) {
      setProposedDocError(validationError);
      return;
    }

    if (originalProjectID) {
      uploadProposedDoc(file, originalProjectID);
    } else {
      setPendingProposedDocFile(file);
    }
  };

  // Opens the uploaded PDF in a new tab. The file endpoint requires the
  // app's own JWT bearer token (same as everything else), which a plain
  // <a href> can't carry -- fetched here as an authenticated blob and
  // handed to the browser as an object URL instead, same pattern
  // BasisOfCostReadOnly.jsx's viewExistingBasisFile already uses.
  const viewProposedDoc = async () => {
    if (!proposedDoc.filename || !originalProjectID) return;
    try {
      const res = await axios.get(`${API_BASE}/projects/proposed_doc/file`, {
        params: { Projectid: originalProjectID, filename: proposedDoc.filename },
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(res.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to open proposed document", err);
      setProposedDocError("Failed to open the document.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "class") {
      let cid = "";
      if (value === "Class 3") cid = "C3";
      if (value === "Class 4") cid = "C4";
      if (value === "Class 5") cid = "C5";
      // FIX: was setForm({ ...form, ... }), reading the `form` variable
      // captured in this closure rather than the freshest state. The Date
      // field (native <input type="date">) can fire its change event in a
      // way that lands after another render already queued from the same
      // interaction, so that stale `form` would win and silently drop the
      // just-picked date back to whatever it was before. The functional
      // updater always applies on top of the latest state instead of a
      // possibly-stale snapshot.
      setForm((prev) => ({ ...prev, class: value, C_ID: cid }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Per request, every Cost Estimation wizard page gets a standalone Save
  // button ahead of Next. Refactored the old goNext() into this shared
  // submitStep1(navigateAfter) so Save and Next run the exact same
  // validate-then-POST logic and only differ in what happens after a
  // successful save: Next moves on to Step 2, Save shows an inline success
  // banner and stays put. Returns the saved Project ID (or null on
  // failure) so a pending Proposed Doc (see pendingProposedDocFile
  // above) can be uploaded the moment either button actually succeeds.
  const submitStep1 = async (navigateAfter) => {
    setServerError(null);
    setSaveMessage(null);
    if (!form.projectName.trim()) { setServerError("Project Name cannot be blank."); return null; }
    if (!form.projectID.trim()) { setServerError("Project ID cannot be blank."); return null; }
    if (!form.class.trim()) { setServerError("Class cannot be blank."); return null; }
    if (!form.C_ID.trim()) { setServerError("C_ID missing. Select Class again."); return null; }

    const payload = {
      project_name: form.projectName,
      projectid: form.projectID,
      // Only a real, already-taken ID (see the mount effect above) is
      // sent here -- lets the backend allow re-saving this exact project
      // under its own unchanged ID while still blocking a new/renamed
      // project from reusing someone else's ID.
      original_projectid: originalProjectID || null,
      // Per explicit request, "Estimated by" is no longer sent from here
      // at all -- the backend now derives/preserves it itself from the
      // authenticated caller's JWT (see create_project_replace in
      // app.py), so it always reflects who actually STARTED the
      // estimation rather than whoever is editing it later.
      estimation_date: form.date || null,
      opco_id: form.opco || null,
      class: form.class,
      c_id: form.C_ID,
      intro: form.intro,
      inflation: form.inflation,
      financial: form.financial,
      contingency: form.contingency,
      risks: form.risks,
      opportunities: form.opportunities,
      status: "P",
      created_by: localStorage.getItem("user_email") || estimatedByEmail || null,
    };

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/projects`, payload);

      if (res.data?.success && res.data?.data?.id) {
        const projectDbId = res.data.data.id;

        localStorage.setItem("projectID", form.projectID);
        localStorage.setItem("projectName", form.projectName);
        // Server-derived (see the payload comment above) -- Step2
        // RiskInflation.jsx/Step5Final.jsx (and their read-only
        // counterparts) still read this key for their own save payloads.
        localStorage.setItem("estimatedBy", res.data.data.estimated_by || estimatedByName);
        localStorage.setItem("estimationDate", form.date);
        localStorage.setItem("operatingCompany", form.opco);

        localStorage.setItem("intro", form.intro);
        localStorage.setItem("inflation", form.inflation);
        localStorage.setItem("financial", form.financial);
        localStorage.setItem("contingency", form.contingency);
        localStorage.setItem("risks", form.risks);
        localStorage.setItem("opportunities", form.opportunities);

        localStorage.setItem("class", form.class);
        localStorage.setItem("C_ID", form.C_ID);

        localStorage.setItem("project_db_id", projectDbId);
        localStorage.setItem("step1", JSON.stringify(form));

        // A save under a brand-new Project ID becomes a re-save of that
        // same project on any later Save/Next click -- same reasoning as
        // ProjectList.jsx's handleEdit(), just derived here instead of via
        // navigation state.
        setOriginalProjectID(form.projectID);

        // FIX: a Proposed Doc picked before this project existed sits in
        // pendingProposedDocFile (see handleProposedDocChange) with no
        // dependency on any field here -- now that a real project row
        // (and folder to attach it under) finally exists, upload it
        // before doing anything else with this successful save. Awaited
        // so it finishes before a Next click navigates this page away.
        // On failure the file simply stays pending (uploadProposedDoc
        // already surfaced the error) and the next successful save
        // retries it.
        if (pendingProposedDocFile) {
          const uploaded = await uploadProposedDoc(pendingProposedDocFile, form.projectID);
          if (uploaded) setPendingProposedDocFile(null);
        }

        if (navigateAfter) {
          navigate(ROUTES.COST_ESTIMATION_STEP2);
        } else {
          setSaveMessage("Project Information saved.");
          setLastSavedTime(new Date());
        }
        return form.projectID;
      } else {
        setServerError(res.data?.message || "Failed to create project.");
        return null;
      }
    } catch (err) {
      console.error(err);
      // axios throws on any non-2xx response, so every validation failure
      // from the backend (duplicate Project ID, missing fields, etc.)
      // lands here -- surface ITS actual message instead of a generic
      // "Server error" for all of them alike.
      const backendMessage = err.response?.data?.message || err.response?.data?.error;
      setServerError(backendMessage || "Server error while creating project. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const goNext = () => submitStep1(true);
  const handleSave = () => submitStep1(false);

  return (
    <>
      {/* Project name in a white block, matching every other step in this
          wizard, bound to form.projectName (the field below) so it updates
          live as the user types. This is the one page where projectName
          can still be genuinely empty (a brand-new estimation, before
          anything's been typed) -- every other wizard step only renders
          once Step1 has already set it. So only here, an empty name falls
          back to the original "Start Cost Estimation" title instead of
          "Project : —". */}
      <div className="page-title-head d-flex align-items-center">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail (same pattern
              as Step2RiskInflation.jsx onward): "Cost Estimation /
              Project / Project : {name}" (or "Start Cost Estimation"
              before a name is set, same fallback this page always had).
              Non-current segments + "/" separators use #02bc9c blended
              90% toward white (a light mint tint, matching the
              reference design's muted breadcrumb segments); the current
              segment stays the full #02bc9c, bold, so it reads as the
              active item -- matching the reference's blue "Portfolio"
              segment. Replaces the earlier #788ca5 bordered/uppercase
              title treatment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Project</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              {form.projectName ? form.projectName : "Start Cost Estimation"}
            </span>
          </nav>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {/* Per request, this page's card background is explicitly
              #FFFFFF -- scoped to just this page via an inline style
              rather than a shared class, so no other page's card is
              affected. */}
          <div className="card" style={{ backgroundColor: "#FFFFFF" }}>
            <div className="card-body">
              {/* Wizard progress bar -- theme's cost-estimation.html has a
                  thin blue bar (.progress > .progress-bar.bg-primary) above
                  the step tabs, driven there by its own single-page tab JS
                  (data-wizard-progress). This app has no such JS since each
                  step is its own route, so the width is just a fixed
                  percentage per page: (this step's position / 7 steps). */}
              <div className="progress mb-4" style={{ height: 6 }}>
                {/* wizard-progress-bar (not bg-primary) -- per request, this
                    bar is brand green (#0f4c42, themeOverrides.css) on all
                    7 wizard stages, not the theme's default blue. Scoped to
                    its own class so the shared bg-primary utility used
                    elsewhere in the app is untouched. */}
                <div className="progress-bar wizard-progress-bar" style={{ width: "14%" }} />
              </div>

              {/* Step indicator -- see the note above WIZARD_STEPS for why
                  only "Project" is a real, clickable tab here. */}
              <ul className="nav nav-tabs wizard-tabs w-100 row g-2 border-0" role="tablist">
                {WIZARD_STEPS.map((step, idx) => (
                  <li className="nav-item col-12 col-lg" key={step.key}>
                    <span
                      className={`nav-link h-100${
                        idx < CURRENT_STEP_INDEX
                          ? " wizard-item-done"
                          : idx === CURRENT_STEP_INDEX
                          ? " wizard-item-current"
                          : " disabled"
                      }`}
                    >
                      <span className="d-flex align-items-center">
                        {step.key === "budget" ? (
                          <svg
                            className="flex-shrink-0"
                            style={{ width: "32px", height: "32px" }}
                            viewBox="0 0 917 1025"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" d="M 916 834 L 570 908 L 550 964 L 542 1001 L 540 1024 L 884 951 L 893 932 L 904 899 L 912 865 Z M 646 54 L 539 144 L 539 473 L 435 496 L 430 1 L 324 89 L 324 518 L 83 570 L 51 682 L 323 629 L 324 767 L 32 830 L 0 946 L 310 881 L 346 863 L 428 746 L 437 604 L 539 584 L 539 803 L 885 730 L 916 614 L 646 670 L 646 561 L 885 509 L 916 397 L 646 449 Z" />
                          </svg>
                        ) : (
                          <i className={`ti ${step.icon} fs-32 flex-shrink-0`} />
                        )}
                        <span className="flex-grow-1 ms-2 text-truncate">
                          <span className="mb-0 lh-base d-block fw-semibold text-body fs-base">{step.label}</span>
                          <span className="fs-xxs mb-0">{step.sub}</span>
                        </span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              {/* Per request, darkened to full opacity -- Bootstrap's
                  default <hr> here was too faint at its default 0.25. */}
              <hr className="mt-3" style={{ opacity: 1 }} />

              {serverError && (
                <div className="alert alert-danger" role="alert">
                  {serverError}
                </div>
              )}

              {/* ===================== PROJECT INFORMATION ===================== */}
              <h5 className="mb-3 text-green fs-4 fw-bold">Project Information</h5>
              {/* Per request, this whole page is now plain white -- the
                  "bg-disabled" class removed here was the last gray tint
                  on the page: it painted this row's own panel gray AND
                  (via app.min.css's ".bg-disabled input, .bg-disabled
                  select" rule) forced every field inside it -- Project
                  Name/ID, Estimated by, Date, OpCo, Class --
                  to that same gray background regardless of their own
                  className. Removing it lets all of them fall back to
                  form-control/form-select's normal white. */}
              <div className="row pt-3">
                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">Project Name</label>
                    <input
                      type="text"
                      name="projectName"
                      className="form-control"
                      placeholder="Enter project name"
                      value={form.projectName}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">Project ID</label>
                    <input
                      type="text"
                      name="projectID"
                      className="form-control"
                      value={form.projectID}
                      onChange={handleChange}
                      // Per request: once a project has actually been
                      // saved (originalProjectID is only ever set when
                      // this page loaded an existing project -- see the
                      // mount effect above), its Project ID can no longer
                      // be changed from here -- Project Name stays
                      // editable, this field alone locks. A brand-new,
                      // not-yet-saved draft still needs to type one in,
                      // so it stays editable until the first save.
                      // FIX, per request: uses readOnly alone now (not
                      // disabled) so the field keeps a plain white
                      // background instead of Bootstrap's grayed-out
                      // disabled look -- still not editable, still
                      // selectable/copyable, just no longer tinted.
                      readOnly={Boolean(originalProjectID)}
                      title={originalProjectID ? "Project ID can't be changed after the project is created." : undefined}
                    />
                  </div>
                </div>

                {/* Per explicit request, replaces the old "Estimated by"
                    field (still auto-tracked -- see estimated_by in
                    submitStep1 -- just no longer shown/editable here).
                    FIX: picking a file has no dependency on any other
                    field on this page or on the project having been
                    saved -- see handleProposedDocChange. If the project
                    doesn't exist yet, the file is just held in
                    pendingProposedDocFile and shown as "selected" below;
                    submitStep1 uploads it the moment Save/Next actually
                    succeeds. After a successful upload (immediate or
                    deferred) the control is swapped for a VIEW/Replace
                    row instead of staying visible alongside the file it
                    just accepted. */}
                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">Proposed Doc</label>
                    {proposedDoc.filename && !showProposedUpload ? (
                      <div className="d-flex align-items-center gap-3">
                        <a
                          href="#"
                          className="fw-semibold"
                          style={{ color: "#02bc9c" }}
                          onClick={(e) => {
                            e.preventDefault();
                            viewProposedDoc();
                          }}
                        >
                          VIEW
                        </a>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => setShowProposedUpload(true)}
                        >
                          Replace
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="form-control"
                          disabled={proposedDocUploading}
                          onChange={handleProposedDocChange}
                        />
                        {/* Set by handleProposedDocChange when a file is chosen
                            before the project exists yet -- no dependency on any
                            other field, just a plain "here's what's queued up"
                            note with a way to change your mind before saving. */}
                        {pendingProposedDocFile && !proposedDocUploading && (
                          <div className="fs-xxs text-muted mt-1">
                            “{pendingProposedDocFile.name}” selected — will be attached when you save.{" "}
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() => setPendingProposedDocFile(null)}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                        {proposedDocUploading && <div className="fs-xxs text-muted mt-1">Uploading...</div>}
                      </>
                    )}
                    {proposedDocError && <div className="fs-xxs text-danger mt-1">{proposedDocError}</div>}
                  </div>
                </div>

                {/* Per request, reordered to OpCo Title -> Class -> Date
                    (was Date -> OpCo Title -> Class). */}
                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">OpCo Title</label>
                    <select
                      name="opco"
                      className="form-select"
                      value={form.opco}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {opcos.map((o) => (
                        <option key={o.OpCo_ID} value={o.OpCo_ID}>
                          {o.OpCo_Name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">
                      Class <span className="text-muted fs-xxs"></span>
                    </label>
                    <select
                      name="class"
                      className="form-select"
                      value={form.class}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {classes.map((c) => (
                        <option key={c.C_ID} value={c.Class}>
                          {c.Class}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">Date</label>
                    <input
                      type="date"
                      name="date"
                      className="form-control"
                      value={form.date}
                      onChange={handleChange}
                      // Per request, reverted back to disabled (not just
                      // readOnly) so this renders as a grayed-out field --
                      // makes it visually obvious it's locked (the date is
                      // always today's) instead of looking like a normal
                      // editable white input.
                      disabled
                      readOnly
                      title="Date is always today's date"
                    />
                  </div>
                </div>
              </div>

              {/* ===================== PROJECT OVERVIEW ===================== */}
              <h5 className="mb-3 mt-2 text-green fs-4 fw-bold">Project Overview</h5>
              <hr className="mt-0 mb-3" style={{ opacity: 1 }} />
              <div className="row">
                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Project Introduction</label>
                    {/* Per request, first field converted from a plain
                        textarea to Rich Text (Bold/Italic/Underline/
                        Lists), with a collapsible formatting toolbar --
                        see RichTextEditor.jsx. form.intro now holds an
                        HTML string instead of plain text. */}
                    <RichTextEditor
                      name="intro"
                      placeholder="Enter project introduction"
                      value={form.intro}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Inflation Forecast</label>
                    <RichTextEditor
                      name="inflation"
                      placeholder="Enter inflation forecast"
                      value={form.inflation}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Financial Overview</label>
                    <RichTextEditor
                      name="financial"
                      placeholder="Enter financial overview"
                      value={form.financial}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Contingency Summary</label>
                    <RichTextEditor
                      name="contingency"
                      placeholder="Enter contingency summary"
                      value={form.contingency}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Risks</label>
                    <RichTextEditor
                      name="risks"
                      placeholder="Enter risks"
                      value={form.risks}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Opportunities</label>
                    <RichTextEditor
                      name="opportunities"
                      placeholder="Enter opportunities"
                      value={form.opportunities}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between mt-3">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.DASHBOARD)}>
                  Back
                </button>
                <div className="d-flex align-items-center gap-2">
                  {/* Per request, same "Last saved <time>" convention as
                      Step5Final.jsx, now shown next to Save on every
                      wizard page. */}
                  {lastSavedTime && (
                    <span className="text-muted fs-13">
                      Last saved {lastSavedTime.toLocaleTimeString()}
                    </span>
                  )}
                  {/* Per request: a standalone Save, ahead of Next, on
                      every Cost Estimation wizard page -- saves this
                      step without navigating away (see submitStep1
                      above). */}
                  <button type="button" className="btn btn-outline-primary" onClick={handleSave} disabled={loading}>
                    {loading ? "Saving..." : "Save"}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={goNext} disabled={loading}>
                    {loading ? "Saving..." : "Next →"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}